import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { ArrowRight, Bike, CheckCircle2, Loader2, XCircle } from "lucide-react";
import SiempriaCredit from "../components/SiempriaCredit";

const T = {
  es: {
    title: "Cambio de moto en tu reserva", intro: "Necesitamos cambiar la moto asignada a tu reserva. No se aplicará ningún cambio hasta que tú lo confirmes aquí.",
    from: "Moto actual", to: "Nueva moto propuesta", dates: "Fechas", ref: "Reserva",
    accept: "Acepto el cambio", reject: "No, prefiero la original",
    acceptedT: "¡Cambio confirmado!", acceptedTxt: "Tu reserva queda con la nueva moto. ¡Nos vemos pronto!",
    rejectedT: "Cambio rechazado", rejectedTxt: "Hemos avisado al equipo. Tu reserva sigue con la moto original.",
    expiredT: "Enlace no válido", expiredTxt: "Este enlace ya no está disponible. Escríbenos por WhatsApp si necesitas ayuda.",
    unavailT: "Ya no disponible", unavailTxt: "Lo sentimos, esa moto ya no está libre en tus fechas. Hemos avisado al equipo para buscarte una alternativa.",
    cancelledT: "Reserva cancelada", cancelledTxt: "Esta reserva está cancelada.",
  },
  en: {
    title: "Bike change on your booking", intro: "We need to change the bike assigned to your booking. Nothing changes until you confirm it here.",
    from: "Current bike", to: "Proposed new bike", dates: "Dates", ref: "Booking",
    accept: "I accept the change", reject: "No, keep the original",
    acceptedT: "Change confirmed!", acceptedTxt: "Your booking now has the new bike. See you soon!",
    rejectedT: "Change rejected", rejectedTxt: "We've notified the team. Your booking keeps the original bike.",
    expiredT: "Invalid link", expiredTxt: "This link is no longer available. Message us on WhatsApp if you need help.",
    unavailT: "No longer available", unavailTxt: "Sorry, that bike is no longer free on your dates. We've notified the team to find you an alternative.",
    cancelledT: "Booking cancelled", cancelledTxt: "This booking is cancelled.",
  },
  de: {
    title: "Motorrad-Wechsel bei deiner Buchung", intro: "Wir müssen das zugewiesene Motorrad wechseln. Es ändert sich nichts, bis du hier bestätigst.",
    from: "Aktuelles Motorrad", to: "Vorgeschlagenes Motorrad", dates: "Daten", ref: "Buchung",
    accept: "Ich akzeptiere", reject: "Nein, Original behalten",
    acceptedT: "Änderung bestätigt!", acceptedTxt: "Deine Buchung hat jetzt das neue Motorrad. Bis bald!",
    rejectedT: "Änderung abgelehnt", rejectedTxt: "Wir haben das Team informiert. Deine Buchung behält das ursprüngliche Motorrad.",
    expiredT: "Ungültiger Link", expiredTxt: "Dieser Link ist nicht mehr verfügbar. Schreib uns bei WhatsApp, wenn du Hilfe brauchst.",
    unavailT: "Nicht mehr verfügbar", unavailTxt: "Leider ist dieses Motorrad an deinen Daten nicht mehr frei. Wir suchen eine Alternative.",
    cancelledT: "Buchung storniert", cancelledTxt: "Diese Buchung ist storniert.",
  },
  it: {
    title: "Cambio moto nella tua prenotazione", intro: "Dobbiamo cambiare la moto assegnata. Nulla cambia finché non confermi qui.",
    from: "Moto attuale", to: "Nuova moto proposta", dates: "Date", ref: "Prenotazione",
    accept: "Accetto il cambio", reject: "No, tengo l'originale",
    acceptedT: "Cambio confermato!", acceptedTxt: "La tua prenotazione ora ha la nuova moto. A presto!",
    rejectedT: "Cambio rifiutato", rejectedTxt: "Abbiamo avvisato il team. La prenotazione mantiene la moto originale.",
    expiredT: "Link non valido", expiredTxt: "Questo link non è più disponibile. Scrivici su WhatsApp se ti serve aiuto.",
    unavailT: "Non più disponibile", unavailTxt: "Spiacenti, quella moto non è più libera nelle tue date. Abbiamo avvisato il team per un'alternativa.",
    cancelledT: "Prenotazione annullata", cancelledTxt: "Questa prenotazione è annullata.",
  },
};

export default function AcceptModelChangePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | pending | accepted | rejected | expired | unavailable | cancelled
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/model-change/${token}`)
      .then(({ data: d }) => {
        setData(d);
        if (d.booking_status === "cancelled") setState("cancelled");
        else setState(d.status === "pending" ? "pending" : d.status);
      })
      .catch(() => setState("expired"));
  }, [token]);

  const t = T[data?.lang] || T.es;

  const act = (action) => {
    setBusy(true);
    axios.post(`${API}/model-change/${token}/${action}`)
      .then(({ data: d }) => { setData(d); setState(d.status); })
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 409) setState("unavailable");
        else if (code === 410) setState("cancelled");
        else setState("expired");
      })
      .finally(() => setBusy(false));
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-topacio-sand flex items-center justify-center px-4 py-10" data-testid="model-change-page">
      <div className="w-full max-w-md bg-white border-2 border-topacio-ink rounded-2xl shadow-[6px_6px_0_0_#111] overflow-hidden">
        <div className="bg-brand px-6 py-4 border-b-2 border-topacio-ink flex items-center gap-2">
          <Bike className="w-5 h-5 text-topacio-ink" />
          <span className="font-black uppercase tracking-wider text-topacio-ink text-sm">Rent a Moto Topacio</span>
        </div>
        <div className="p-6">{children}</div>
        <div className="px-6 pb-6"><SiempriaCredit /></div>
      </div>
    </div>
  );

  if (state === "loading") {
    return <Shell><div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-topacio-ink" /></div></Shell>;
  }

  if (state === "pending") {
    return (
      <Shell>
        <h1 className="text-xl font-black text-topacio-ink">{t.title} {data.ref}</h1>
        <p className="text-sm text-topacio-faded mt-2 mb-5">{t.intro}</p>
        <div className="space-y-3 mb-6">
          <div className="rounded-xl border-2 border-topacio-ink/15 p-3">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">{t.from}</div>
            <div className="font-bold text-topacio-ink line-through decoration-topacio-red/60" data-testid="mc-from">{data.from_name}</div>
          </div>
          <div className="flex justify-center"><ArrowRight className="w-5 h-5 text-topacio-faded" /></div>
          <div className="rounded-xl border-2 border-topacio-green p-3 bg-topacio-green/5">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-green">{t.to}</div>
            <div className="font-black text-topacio-ink" data-testid="mc-to">{data.to_name}</div>
          </div>
          <div className="text-xs text-topacio-faded pt-1">{t.dates}: <strong className="text-topacio-ink">{data.start_date} → {data.end_date}</strong></div>
        </div>
        <button data-testid="mc-accept-btn" disabled={busy} onClick={() => act("accept")}
          className="btn-primary w-full !py-3 mb-3 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t.accept}
        </button>
        <button data-testid="mc-reject-btn" disabled={busy} onClick={() => act("reject")}
          className="btn-secondary w-full !py-3 flex items-center justify-center gap-2">
          <XCircle className="w-4 h-4" /> {t.reject}
        </button>
      </Shell>
    );
  }

  const results = {
    accepted: { icon: <CheckCircle2 className="w-14 h-14 text-topacio-green" />, title: t.acceptedT, txt: t.acceptedTxt },
    rejected: { icon: <XCircle className="w-14 h-14 text-topacio-ink" />, title: t.rejectedT, txt: t.rejectedTxt },
    unavailable: { icon: <XCircle className="w-14 h-14 text-topacio-red" />, title: t.unavailT, txt: t.unavailTxt },
    expired: { icon: <XCircle className="w-14 h-14 text-topacio-faded" />, title: t.expiredT, txt: t.expiredTxt },
    cancelled: { icon: <XCircle className="w-14 h-14 text-topacio-faded" />, title: t.cancelledT, txt: t.cancelledTxt },
  };
  const r = results[state] || results.expired;
  return (
    <Shell>
      <div className="flex flex-col items-center text-center py-6" data-testid={`mc-result-${state}`}>
        {r.icon}
        <h1 className="text-xl font-black text-topacio-ink mt-4">{r.title}</h1>
        <p className="text-sm text-topacio-faded mt-2">{r.txt}</p>
      </div>
    </Shell>
  );
}
